import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar, Button, Card, Drawer, Select, Space, Spin, Tag, Typography, message,
} from 'antd';
import { UserOutlined, ArrowRightOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { getAdminCropMapApi, getAdminCropUserStatesApi } from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import UserCropsTable, { YEAR_OPTIONS } from './UserCropsTable';
import CropCommonIdSelect from './CropCommonIdSelect';

const { Title, Text } = Typography;

const INDIA_CENTER = [22.5, 79];
const ACTIVE_COLOR    = '#1a6b2a';
const HARVESTED_COLOR = '#d4a017';

// Zooms the map to the markers whenever the result set changes.
function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 11);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [30, 30], maxZoom: 12 });
  }, [map, points]);
  return null;
}

export default function AdminCropMap() {
  const navigate = useNavigate();

  const [stateOptions, setStateOptions] = useState([]);
  const [commonId, setCommonId]         = useState(null);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [year, setYear]                 = useState(new Date().getFullYear());
  const [state, setState]               = useState(null);

  const [points, setPoints]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    getAdminCropUserStatesApi()
      .then((res) => setStateOptions(res.data?.data || []))
      .catch(() => setStateOptions([]));
  }, []);

  useEffect(() => {
    if (commonId == null) { setPoints([]); setSummary(null); return; }
    let cancelled = false;
    setLoading(true);
    const params = { common_id: commonId };
    if (year)  params.year  = year;
    if (state) params.state = state;
    getAdminCropMapApi(params)
      .then((res) => {
        if (cancelled) return;
        setPoints(res.data?.data || []);
        setSummary(res.data?.summary || null);
      })
      .catch(() => { if (!cancelled) message.error('Failed to load crop map'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [commonId, year, state]);

  const activeUsers = useMemo(() => points.filter((p) => Number(p.active_count) > 0).length, [points]);

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>Crop Map</Title>

      <Card style={{ borderRadius: 10, marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
        <Space wrap size={12} style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap size={12}>
            <CropCommonIdSelect
              value={commonId}
              onChange={(id, crop) => { setCommonId(id); setSelectedCrop(crop); }}
            />
            <Select
              value={year}
              onChange={(v) => setYear(v ?? null)}
              options={YEAR_OPTIONS}
              placeholder="All years"
              allowClear
              style={{ width: 130 }}
            />
            <Select
              showSearch
              value={state}
              onChange={(v) => setState(v ?? null)}
              placeholder="All states"
              allowClear
              optionFilterProp="label"
              style={{ width: 200 }}
              options={stateOptions.map((s) => ({ value: s.state, label: s.state }))}
            />
          </Space>
          {summary && (
            <Space size={8} wrap>
              <Tag icon={<EnvironmentOutlined />} color="green">
                {summary.users_with_location} of {summary.total_users} users on map
              </Tag>
              <Tag color={ACTIVE_COLOR}>{activeUsers} growing now</Tag>
              <Tag color={HARVESTED_COLOR}>{points.length - activeUsers} harvested</Tag>
            </Space>
          )}
        </Space>
      </Card>

      <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: 10 } }}>
        <Spin spinning={loading}>
          <div style={{ position: 'relative' }}>
            <MapContainer
              center={INDIA_CENTER}
              zoom={5}
              preferCanvas
              style={{ height: 'calc(100vh - 260px)', minHeight: 420, width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              <FitToPoints points={points} />
              {points.map((p) => {
                const color = Number(p.active_count) > 0 ? ACTIVE_COLOR : HARVESTED_COLOR;
                return (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lng]}
                    radius={7}
                    pathOptions={{ color: '#fff', weight: 1.5, fillColor: color, fillOpacity: 0.9 }}
                  >
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name || '-'}</div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>{p.country_code} {p.mobile}</div>
                        {(p.city || p.state) && (
                          <div style={{ color: '#6b7280', fontSize: 12 }}>
                            {[p.city, p.state].filter(Boolean).join(', ')}
                          </div>
                        )}
                        <div style={{ margin: '8px 0' }}>
                          <Tag color="green">{p.crops_count} {selectedCrop?.name || 'crop'}</Tag>
                          {Number(p.active_count) > 0 && <Tag color="blue">{p.active_count} active</Tag>}
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 8 }}>Last sown: {formatDate(p.last_crop_date)}</div>
                        <Button size="small" type="primary" onClick={() => setSelectedUser(p)}>
                          View crops
                        </Button>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
            {commonId == null && (
              <div style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
                background: '#fff', padding: '6px 14px', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                <Text type="secondary">Select a crop to see where it is grown</Text>
              </div>
            )}
          </div>
        </Spin>
      </Card>

      <Drawer
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        width={Math.min(1100, window.innerWidth)}
        destroyOnHidden
        title={selectedUser && (
          <Space>
            <Avatar
              src={selectedUser.photo ? USER_IMAGE(selectedUser.photo) : null}
              icon={<UserOutlined />}
              style={{ background: '#e8f5e9', color: '#1a6b2a' }}
            />
            <div>
              <div>{selectedUser.name}</div>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                {selectedUser.country_code} {selectedUser.mobile}
                {selectedUser.state ? ` · ${selectedUser.state}` : ''}
              </Text>
            </div>
          </Space>
        )}
        extra={selectedUser && (
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => navigate(`/admin/users/${selectedUser.id}`, { state: { user: selectedUser, tab: 'crops' } })}
          >
            Open profile
          </Button>
        )}
      >
        {selectedUser && (
          <UserCropsTable userId={selectedUser.id} highlightCommonId={commonId} />
        )}
      </Drawer>
    </div>
  );
}
